import { supabase } from '../utils/supabaseClient';
import { mergeBusinessHours } from '../utils/availability';
import { withCrmEnabled } from '../utils/crmUtils';
import type { Agent } from '../types/Agents';
import type { Message } from '../types/Message';
import type {
  Booking,
  CreateBookingInput,
  UpdateBookingInput,
} from '../types/Booking';
import type {
  Service,
  CreateServiceInput,
  UpdateServiceInput,
} from '../types/Service';
import type { Contact } from '../types/Contact';
import type { AppNotification } from '../types/Notification';
import i18next from 'i18next';

export interface CreateAgentInput extends Omit<Agent, 'id'> {
  business_hours: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    is_closed: boolean;
  }>;
  services: Array<{
    name: string;
    description: string | null;
    duration_minutes: number;
    price: number | null;
    enabled: boolean;
  }>;
}

// Interface for enhanced transcript data structure
export interface EnhancedTranscriptSegment {
  text: string;
  speaker: string;
  timestamp: string;
}

export interface EnhancedTranscriptData {
  segments: EnhancedTranscriptSegment[];
}

export interface EnhancedTranscript {
  id: number;
  conversation_id: string;
  transcript_data: EnhancedTranscriptData;
  created_at: string;
}

// Interface for conversation summary data structure
export interface ConversationSummary {
  id: number;
  conversation_id: string;
  summary_text: string;
  created_at: string;
}

async function attachCrmFlags(agents: Agent[]): Promise<Agent[]> {
  const ids = agents
    .map(agent => agent.id)
    .filter((id): id is number => typeof id === 'number');

  if (ids.length === 0) return agents.map(withCrmEnabled);

  const { data, error } = await supabase.rpc('agent_crm_flags', {
    p_agent_ids: ids,
  });

  if (error || !data) return agents.map(withCrmEnabled);

  const flagById = new Map<number, boolean>(
    (data as { agent_id: number; crm_enabled: boolean }[]).map(row => [
      Number(row.agent_id),
      Boolean(row.crm_enabled),
    ])
  );

  return agents.map(agent => {
    if (agent.id != null && flagById.has(agent.id)) {
      return { ...agent, crm_enabled: flagById.get(agent.id) as boolean };
    }
    return withCrmEnabled(agent);
  });
}

function normalizeAgentRelations(agent: Agent): Agent {
  return {
    ...agent,
    contacts: agent.contacts ?? [],
    conversations: (agent.conversations ?? []).map(conversation => ({
      ...conversation,
      messages: conversation.messages ?? [],
    })),
  };
}

export const agentsApi = {
  fetchAgents: async () => {
    const { data, error } = await supabase.rpc('get_agents');
    if (error)
      throw new Error(
        i18next.t('api.agents.errors.fetch_failed.translation', {
          error: error.message,
        })
      );
    // get_agents returns null (not []) when the user has no agents or when
    // nested jsonb_agg relationships (contacts, conversations, messages) have 0 rows
    const agents = ((data ?? []) as Agent[]).map(normalizeAgentRelations);
    return attachCrmFlags(agents);
  },

  createAgent: async (agent: CreateAgentInput) => {
    const { data: insertedAgent, error } = await supabase.rpc(
      'create_agent_with_setup',
      {
        p_name: agent.name,
        p_context: agent.context,
        p_description: agent.description ?? null,
        p_notification_phone_number: agent.notification_phone_number ?? null,
        p_business_info: agent.business_info ?? {},
        p_business_hours: agent.business_hours,
        p_services: agent.services,
        p_summary_email_delivery:
          agent.config?.notifications?.summary_email_delivery ?? 'digest',
        p_summary_digest_hour:
          agent.config?.notifications?.summary_digest_hour ?? 8,
      }
    );

    if (error)
      throw new Error(
        i18next.t('api.agents.errors.create_failed.translation', {
          error: error.message,
        })
      );

    const insertedAgentId = Number(
      (insertedAgent as Record<string, unknown> | null)?.id
    );
    if (!Number.isFinite(insertedAgentId)) {
      throw new Error(
        i18next.t('api.agents.errors.create_failed.translation', {
          error: 'The database did not return the created agent ID.',
        })
      );
    }

    let fullAgent: Agent | null = null;
    try {
      const { data, error: refetchError } = await supabase
        .from('agents')
        .select('*, subscription(*)')
        .eq('id', insertedAgentId)
        .single();

      if (refetchError) {
        console.warn(
          'Agent created but post-create refetch failed.',
          refetchError
        );
      } else {
        fullAgent = data as Agent;
      }
    } catch (refetchError) {
      console.warn(
        'Agent created but post-create refetch failed.',
        refetchError
      );
    }

    const [createdAgent] = await attachCrmFlags([
      normalizeAgentRelations(fullAgent ?? (insertedAgent as Agent)),
    ]);
    return { agent: createdAgent };
  },

  updateAgentSettings: async ({
    agentId,
    settings,
  }: {
    agentId: number;
    settings: Partial<Omit<Agent, 'notification_phone_number'>> & {
      notification_phone_number?: string | null;
    };
  }) => {
    // Status is managed by backend subscription lifecycle, not this settings endpoint.
    if ('status' in settings) {
      throw new Error(
        i18next.t('api.agents.errors.status_update_not_allowed.translation', {
          suggestion: 'Agent status is managed by backend automation',
        })
      );
    }

    const payload: {
      name?: string;
      context?: string;
      description?: string;
      notification_phone_number?: string | null;
      business_info?: Agent['business_info'];
      config?: Agent['config'];
    } = {};
    if (settings.name !== undefined) payload.name = settings.name;
    if (settings.context !== undefined) payload.context = settings.context;
    if (settings.description !== undefined) {
      payload.description = settings.description;
    }
    if (settings.notification_phone_number !== undefined) {
      payload.notification_phone_number = settings.notification_phone_number;
    }
    if (settings.business_info !== undefined) {
      payload.business_info = settings.business_info ?? {};
    }
    if (settings.config !== undefined) {
      payload.config = settings.config;
    }

    const { data, error } = await supabase
      .from('agents')
      .update(payload)
      .eq('id', agentId)
      .select()
      .single();

    if (error)
      throw new Error(
        i18next.t('api.agents.errors.update_failed.translation', {
          error: error.message,
        })
      );
    return withCrmEnabled(data as Agent);
  },

  handleDelete: async (agentId: number) => {
    try {
      const { error } = await supabase.rpc('delete_agent_flow', {
        p_agent_id: agentId,
      });

      if (error)
        throw new Error(
          i18next.t('api.agents.errors.delete_failed.translation', {
            error: error.message,
          })
        );

      return true;
    } catch (error) {
      throw new Error(
        i18next.t('api.agents.errors.delete_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },
};

export const enhancedTranscriptApi = {
  fetchEnhancedTranscript: async (
    conversationId: string
  ): Promise<EnhancedTranscript | null> => {
    try {
      const { data, error } = await supabase
        .from('enhanced_transcripts')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No enhanced transcript found - this is expected for some conversations
          return null;
        }
        throw new Error(
          i18next.t('api.enhanced_transcript.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as EnhancedTranscript;
    } catch (error) {
      throw new Error(
        i18next.t('api.enhanced_transcript.errors.fetch_failed.translation', {
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      );
    }
  },

  transformEnhancedTranscriptToMessages: (
    transcript: EnhancedTranscript
  ): Message[] => {
    // Validate transcript data structure
    if (
      !transcript.transcript_data ||
      !Array.isArray(transcript.transcript_data.segments)
    ) {
      return [];
    }

    return transcript.transcript_data.segments
      .filter(segment => segment.text && segment.speaker && segment.timestamp) // Filter out invalid segments
      .map((segment, index) => ({
        id: index + 1, // Generate sequential IDs
        conversation_id: transcript.conversation_id,
        role: segment.speaker.toLowerCase() === 'user' ? 'user' : 'assistant',
        content: segment.text,
        created_at: segment.timestamp,
      }));
  },
};

export const conversationSummariesApi = {
  fetchConversationSummary: async (
    conversationId: string
  ): Promise<ConversationSummary | null> => {
    try {
      const { data, error } = await supabase
        .from('conversation_summaries')
        .select('*')
        .eq('conversation_id', conversationId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No conversation summary found - this is expected for some conversations
          return null;
        }
        throw new Error(
          i18next.t(
            'api.conversation_summary.errors.fetch_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }

      return data as ConversationSummary;
    } catch (error) {
      throw new Error(
        i18next.t('api.conversation_summary.errors.fetch_failed.translation', {
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      );
    }
  },
};

export const conversationAudioApi = {
  generatePresignedUrl: async (conversationId: string) => {
    const { data, error } = await supabase.functions.invoke(
      'generate-conversation-presigned-url',
      {
        body: {
          conversation_id: conversationId,
          conversationId: conversationId,
        },
      }
    );

    if (error) {
      throw new Error(
        i18next.t(
          'api.conversation_audio.errors.presigned_url_failed.translation',
          {
            error: error.message,
          }
        )
      );
    }

    return data;
  },
};

export const contactsApi = {
  createContact: async (
    agentId: number,
    callerId: string,
    name?: string
  ): Promise<Contact> => {
    try {
      const { data, error } = await supabase
        .from('agent_contacts')
        .insert([
          {
            agent_id: agentId,
            caller_id: callerId,
            name: name || callerId,
          },
        ])
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.contacts.errors.create_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Contact;
    } catch (error) {
      throw new Error(
        i18next.t('api.contacts.errors.create_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  updateContact: async (contactId: number, updates: { name: string }) => {
    const { data, error } = await supabase
      .from('agent_contacts')
      .update(updates)
      .eq('id', contactId)
      .select()
      .single();

    if (error) {
      throw new Error(
        i18next.t('api.contacts.errors.update_failed.translation', {
          error: error.message,
        })
      );
    }

    return data;
  },
};

export const subscriptionPlansApi = {
  fetchSubscriptionPlans: async () => {
    const { data, error } = await supabase
      .from('subscription_plan')
      .select('*')
      .order('price', { ascending: true });

    if (error) {
      throw new Error(
        i18next.t('api.subscription_plans.errors.fetch_failed.translation', {
          error: error.message,
        })
      );
    }

    return data;
  },

  createSubscription: async ({
    agentId,
    subscriptionPlanId,
  }: {
    agentId: number;
    subscriptionPlanId: number;
  }) => {
    try {
      // First, check if there's an existing subscription for this agent
      const { data: existingSubscription, error: fetchError } = await supabase
        .from('subscription')
        .select('*')
        .eq('agent_id', agentId)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        // PGRST116 means no rows found, which is expected for new subscriptions
        throw new Error(
          i18next.t('api.subscription.errors.fetch_failed.translation', {
            error: fetchError.message,
          })
        );
      }

      if (existingSubscription) {
        // Update existing subscription
        const { data, error } = await supabase
          .from('subscription')
          .update({
            subscription_plan_id: subscriptionPlanId,
            status: 'requested',
          })
          .eq('agent_id', agentId)
          .select()
          .single();

        if (error) {
          throw new Error(
            i18next.t('api.subscription.errors.update_failed.translation', {
              error: error.message,
            })
          );
        }

        return data;
      } else {
        // Create new subscription
        const { data, error } = await supabase
          .from('subscription')
          .insert([
            {
              agent_id: agentId,
              subscription_plan_id: subscriptionPlanId,
              status: 'requested',
            },
          ])
          .select()
          .single();

        if (error) {
          throw new Error(
            i18next.t('api.subscription.errors.create_failed.translation', {
              error: error.message,
            })
          );
        }

        return data;
      }
    } catch (error) {
      throw new Error(
        i18next.t('api.subscription.errors.operation_failed.translation', {
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      );
    }
  },

  cancelSubscription: async (agentId: number) => {
    try {
      const { data, error } = await supabase.rpc('cancel_subscription', {
        p_agent_id: agentId,
      });

      if (error) {
        throw new Error(
          i18next.t('api.subscription.errors.cancel_failed.translation', {
            error: error.message,
          })
        );
      }

      return data;
    } catch (error) {
      throw new Error(
        i18next.t('api.subscription.errors.cancel_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },
};

export interface BusinessHour {
  id?: number;
  agent_id: number;
  day_of_week: number; // 1 = Monday, 2 = Tuesday, ..., 7 = Sunday
  start_time: string; // Supabase time; usually HH:mm or HH:mm:ss
  end_time: string; // Supabase time; usually HH:mm or HH:mm:ss
  is_closed: boolean;
  created_at?: string;
}

export const businessHoursApi = {
  fetchBusinessHours: async (agentId: number): Promise<BusinessHour[]> => {
    try {
      const { data, error } = await supabase
        .from('crm_business_hours')
        .select('*')
        .eq('agent_id', agentId)
        .order('day_of_week', { ascending: true });

      if (error) {
        throw new Error(
          i18next.t('api.business_hours.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return mergeBusinessHours(agentId, data ?? []);
    } catch (error) {
      throw new Error(
        i18next.t('api.business_hours.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  upsertBusinessHours: async (
    agentId: number,
    businessHours: Omit<BusinessHour, 'id' | 'agent_id' | 'created_at'>[]
  ): Promise<BusinessHour[]> => {
    try {
      // First, delete existing business hours for this agent
      const { error: deleteError } = await supabase
        .from('crm_business_hours')
        .delete()
        .eq('agent_id', agentId);

      if (deleteError) {
        throw new Error(
          i18next.t('api.business_hours.errors.delete_failed.translation', {
            error: deleteError.message,
          })
        );
      }

      // Insert new business hours
      const hoursToInsert = businessHours.map(hour => ({
        agent_id: agentId,
        day_of_week: hour.day_of_week,
        start_time: hour.start_time,
        end_time: hour.end_time,
        is_closed: hour.is_closed,
      }));

      const { data, error } = await supabase
        .from('crm_business_hours')
        .insert(hoursToInsert)
        .select();

      if (error) {
        throw new Error(
          i18next.t('api.business_hours.errors.save_failed.translation', {
            error: error.message,
          })
        );
      }

      return data || [];
    } catch (error) {
      throw new Error(
        i18next.t('api.business_hours.errors.save_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },
};

export interface AvailabilityOverride {
  id?: number;
  agent_id: number;
  date: string; // ISO date string (YYYY-MM-DD)
  start_time: string; // Supabase time; usually HH:mm or HH:mm:ss
  end_time: string; // Supabase time; usually HH:mm or HH:mm:ss
  is_closed: boolean;
  created_at?: string;
}

export const availabilityOverridesApi = {
  fetchByAgent: async (agentId: number): Promise<AvailabilityOverride[]> => {
    try {
      const { data, error } = await supabase
        .from('crm_availability_overrides')
        .select('*')
        .eq('agent_id', agentId)
        .order('date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(
          i18next.t(
            'api.availability_overrides.errors.fetch_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }

      return (data || []) as AvailabilityOverride[];
    } catch (error) {
      throw new Error(
        i18next.t(
          'api.availability_overrides.errors.fetch_failed.translation',
          {
            error:
              error instanceof Error
                ? error.message
                : i18next.t('common.unknown_error.translation'),
          }
        )
      );
    }
  },

  fetchByAgentAndDateRange: async (
    agentId: number,
    startDate: Date,
    endDate: Date
  ): Promise<AvailabilityOverride[]> => {
    try {
      const { data, error } = await supabase
        .from('crm_availability_overrides')
        .select('*')
        .eq('agent_id', agentId)
        .gte('date', startDate.toISOString().slice(0, 10))
        .lte('date', endDate.toISOString().slice(0, 10))
        .order('date', { ascending: true });

      if (error) {
        throw new Error(
          i18next.t(
            'api.availability_overrides.errors.fetch_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }

      return (data || []) as AvailabilityOverride[];
    } catch (error) {
      throw new Error(
        i18next.t(
          'api.availability_overrides.errors.fetch_failed.translation',
          {
            error:
              error instanceof Error
                ? error.message
                : i18next.t('common.unknown_error.translation'),
          }
        )
      );
    }
  },

  create: async (
    override: Omit<AvailabilityOverride, 'id' | 'created_at'>
  ): Promise<AvailabilityOverride> => {
    try {
      const { data, error } = await supabase
        .from('crm_availability_overrides')
        .insert([override])
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t(
            'api.availability_overrides.errors.create_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }

      return data as AvailabilityOverride;
    } catch (error) {
      throw new Error(
        i18next.t(
          'api.availability_overrides.errors.create_failed.translation',
          {
            error:
              error instanceof Error
                ? error.message
                : i18next.t('common.unknown_error.translation'),
          }
        )
      );
    }
  },

  update: async (
    id: number,
    updates: Partial<
      Omit<AvailabilityOverride, 'id' | 'agent_id' | 'created_at'>
    >
  ): Promise<AvailabilityOverride> => {
    try {
      const { data, error } = await supabase
        .from('crm_availability_overrides')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t(
            'api.availability_overrides.errors.update_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }

      return data as AvailabilityOverride;
    } catch (error) {
      throw new Error(
        i18next.t(
          'api.availability_overrides.errors.update_failed.translation',
          {
            error:
              error instanceof Error
                ? error.message
                : i18next.t('common.unknown_error.translation'),
          }
        )
      );
    }
  },

  delete: async (id: number): Promise<void> => {
    try {
      const { error } = await supabase
        .from('crm_availability_overrides')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(
          i18next.t(
            'api.availability_overrides.errors.delete_failed.translation',
            {
              error: error.message,
            }
          )
        );
      }
    } catch (error) {
      throw new Error(
        i18next.t(
          'api.availability_overrides.errors.delete_failed.translation',
          {
            error:
              error instanceof Error
                ? error.message
                : i18next.t('common.unknown_error.translation'),
          }
        )
      );
    }
  },
};

export const bookingsApi = {
  fetchBookings: async (
    agentId: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<Booking[]> => {
    try {
      let query = supabase
        .from('crm_bookings')
        .select('*')
        .eq('agent_id', agentId)
        .order('start_time', { ascending: true });

      if (startDate) {
        query = query.gte('start_time', startDate.toISOString());
      }

      if (endDate) {
        query = query.lte('start_time', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return (data || []) as Booking[];
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  fetchBookingById: async (bookingId: number): Promise<Booking> => {
    try {
      const { data, error } = await supabase
        .from('crm_bookings')
        .select('*')
        .eq('id', bookingId)
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Booking;
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  fetchBookingsByContactId: async (
    agentId: number,
    contactId: number
  ): Promise<Booking[]> => {
    try {
      const { data, error } = await supabase
        .from('crm_bookings')
        .select('*')
        .eq('agent_id', agentId)
        .eq('contact_id', contactId)
        .order('start_time', { ascending: false });

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return (data || []) as Booking[];
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  fetchBookingByConversationId: async (
    agentId: number,
    conversationId: number
  ): Promise<Booking | null> => {
    try {
      // Either link, matching the rule get_agents and user_notifications use
      // to decide a call's booking outcome: conversation_id is the call that
      // created the booking, last_conversation_id the call that moved or
      // cancelled it. Matching only the first meant a "Moved" or "Cancelled"
      // badge in the list opened a conversation with no appointment on it.
      //
      // Ordered and limited rather than maybeSingle: a call can touch more
      // than one booking, and the newest start time is the one the summary
      // is about.
      const { data, error } = await supabase
        .from('crm_bookings')
        .select('*')
        .eq('agent_id', agentId)
        .or(
          `conversation_id.eq.${conversationId},last_conversation_id.eq.${conversationId}`
        )
        .order('start_time', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return (data as Booking) ?? null;
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  createBooking: async (booking: CreateBookingInput): Promise<Booking> => {
    try {
      const { data, error } = await supabase
        .from('crm_bookings')
        .insert([booking])
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.create_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Booking;
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.create_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  updateBooking: async (
    bookingId: number,
    updates: UpdateBookingInput
  ): Promise<Booking> => {
    try {
      const { data, error } = await supabase
        .from('crm_bookings')
        .update(updates)
        .eq('id', bookingId)
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.update_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Booking;
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.update_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  deleteBooking: async (bookingId: number): Promise<void> => {
    try {
      const { error } = await supabase
        .from('crm_bookings')
        .delete()
        .eq('id', bookingId);

      if (error) {
        throw new Error(
          i18next.t('api.bookings.errors.delete_failed.translation', {
            error: error.message,
          })
        );
      }
    } catch (error) {
      throw new Error(
        i18next.t('api.bookings.errors.delete_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },
};

export interface ConversationCallbackRequest {
  id: number;
  conversation_id: number;
  status: string;
  reason: string | null;
  created_at: string;
}

export const conversationCallbackApi = {
  /**
   * The open callback request for a conversation, if the agent raised one.
   *
   * `reason` is written by the agent's requestHumanCallback tool, which is
   * told to derive a short reason from the live conversation -- so it is
   * already a sentence written for a person to read.
   */
  fetchByConversation: async (
    conversationId: number
  ): Promise<ConversationCallbackRequest | null> => {
    const { data, error } = await supabase
      .from('conversation_callback_requests')
      .select('id, conversation_id, status, reason, created_at')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      throw new Error(
        i18next.t('api.callback_requests.errors.fetch_failed.translation', {
          defaultValue: 'Failed to load the callback request: {{error}}',
          error: error.message,
        })
      );
    }

    return (data as ConversationCallbackRequest) ?? null;
  },
};

export const servicesApi = {
  fetchServices: async (agentId: number): Promise<Service[]> => {
    try {
      const { data, error } = await supabase
        .from('crm_services')
        .select('*')
        .eq('agent_id', agentId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(
          i18next.t('api.services.errors.fetch_failed.translation', {
            error: error.message,
          })
        );
      }

      return (data || []) as Service[];
    } catch (error) {
      throw new Error(
        i18next.t('api.services.errors.fetch_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  createService: async (service: CreateServiceInput): Promise<Service> => {
    try {
      const { data, error } = await supabase
        .from('crm_services')
        .insert([service])
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.services.errors.create_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Service;
    } catch (error) {
      throw new Error(
        i18next.t('api.services.errors.create_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  updateService: async (
    serviceId: number,
    updates: UpdateServiceInput
  ): Promise<Service> => {
    try {
      const { data, error } = await supabase
        .from('crm_services')
        .update(updates)
        .eq('id', serviceId)
        .select()
        .single();

      if (error) {
        throw new Error(
          i18next.t('api.services.errors.update_failed.translation', {
            error: error.message,
          })
        );
      }

      return data as Service;
    } catch (error) {
      throw new Error(
        i18next.t('api.services.errors.update_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  deleteService: async (serviceId: number): Promise<void> => {
    try {
      const { error } = await supabase
        .from('crm_services')
        .delete()
        .eq('id', serviceId);

      if (error) {
        throw new Error(
          i18next.t('api.services.errors.delete_failed.translation', {
            error: error.message,
          })
        );
      }
    } catch (error) {
      throw new Error(
        i18next.t('api.services.errors.delete_failed.translation', {
          error:
            error instanceof Error
              ? error.message
              : i18next.t('common.unknown_error.translation'),
        })
      );
    }
  },

  toggleServiceEnabled: async (
    serviceId: number,
    enabled: boolean
  ): Promise<Service> => {
    return servicesApi.updateService(serviceId, { enabled });
  },
};

export const NOTIFICATIONS_PAGE_SIZE = 20;

export const notificationsApi = {
  /**
   * One page of the feed, newest first. `before` is the `created_at` of the
   * oldest row already held, so paging stays stable while new rows arrive at
   * the top.
   */
  fetchNotifications: async (before?: string): Promise<AppNotification[]> => {
    let query = supabase
      .from('user_notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(NOTIFICATIONS_PAGE_SIZE);

    if (before) {
      query = query.lt('created_at', before);
    }

    const { data, error } = await query;

    if (error) {
      throw new Error(
        i18next.t('api.notifications.errors.fetch_failed.translation', {
          error: error.message,
        })
      );
    }

    return (data ?? []) as AppNotification[];
  },

  /** Unread badge count, fetched without transferring the rows themselves. */
  fetchUnreadCount: async (): Promise<number> => {
    const { count, error } = await supabase
      .from('user_notifications')
      .select('id', { count: 'exact', head: true })
      .is('read_at', null);

    if (error) {
      throw new Error(
        i18next.t('api.notifications.errors.fetch_failed.translation', {
          error: error.message,
        })
      );
    }

    return count ?? 0;
  },

  /**
   * Marks notifications read. Omit `ids` to mark every unread one.
   * Returns the ids actually changed, so a stale optimistic update can be
   * reconciled against what the database really did.
   */
  markRead: async (ids?: number[]): Promise<number[]> => {
    const { data, error } = await supabase.rpc('mark_notifications_read', {
      p_ids: ids ?? null,
    });

    if (error) {
      throw new Error(
        i18next.t('api.notifications.errors.mark_read_failed.translation', {
          error: error.message,
        })
      );
    }

    // The function returns `setof bigint`, which PostgREST surfaces either as a
    // bare array or as one-key row objects depending on the client version.
    if (!Array.isArray(data)) return [];
    return data.map(row =>
      typeof row === 'number' ? row : Number(Object.values(row ?? {})[0])
    );
  },

  /** Permanently removes the caller's own notifications. */
  clearAll: async (): Promise<void> => {
    const { error } = await supabase
      .from('user_notifications')
      .delete()
      .not('id', 'is', null);

    if (error) {
      throw new Error(
        i18next.t('api.notifications.errors.clear_failed.translation', {
          error: error.message,
        })
      );
    }
  },
};
